import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import JoditEditor from 'jodit-react';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';
import { getJoditConfig } from '../../utils/joditConfig';
import MultiSelect from '../../components/MultiSelect';
import ConfirmModal from '../../components/ConfirmModal';

const AddBlog = () => {
  const navigate = useNavigate();
  const descriptionEditor = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [showBackModal, setShowBackModal] = useState(false);
  const [packages, setPackages] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    sortDescription: '',
    latitude: '',
    longitude: '',
    writer: '',
    blogCategory: '',
    metaTitle: '',
    metaScript: '',
    metaKeyword: '',
    metaDescription: '',
    type: 'travellinglark',
    packages: []
  });
  const [description, setDescription] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [faqs, setFaqs] = useState([{ question: '', answer: '' }]);
  const [recentFaqs, setRecentFaqs] = useState([{ question: '', answer: '' }]);
  const [keywordLinks, setKeywordLinks] = useState([{ keyword: '', link: '' }]);

  const editorConfig = useMemo(() => getJoditConfig(), []);

  useEffect(() => {
    fetchInitialData();

    // Push history state to intercept browser back button and 2-finger swipe back gesture
    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
      setShowBackModal(true);
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const fetchInitialData = async () => {
    try {
      const response = await apiService.get('addBlog');
      const packagesData = response?.data?.packages || response?.packages || [];
      const pkgs = packagesData.map(p => ({
        ...p,
        name: p.vendorId?.business_name ? `${p.name} (${p.vendorId.business_name})` : p.name
      }));
      setPackages(pkgs);
    } catch (error) {
      console.error('Failed to fetch packages', error);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('File size must be 2MB or less');
        e.target.value = '';
        return;
      }
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const addFaq = () => setFaqs([...faqs, { question: '', answer: '' }]);
  const removeFaq = (index) => setFaqs(faqs.filter((_, i) => i !== index));
  const updateFaq = (index, field, value) => {
    const updated = [...faqs];
    updated[index][field] = value;
    setFaqs(updated);
  };

  const addRecentFaq = () => setRecentFaqs([...recentFaqs, { question: '', answer: '' }]);
  const removeRecentFaq = (index) => setRecentFaqs(recentFaqs.filter((_, i) => i !== index));
  const updateRecentFaq = (index, field, value) => {
    const updated = [...recentFaqs];
    updated[index][field] = value;
    setRecentFaqs(updated);
  };

  const addKeywordLink = () => setKeywordLinks([...keywordLinks, { keyword: '', link: '' }]);
  const removeKeywordLink = (index) => setKeywordLinks(keywordLinks.filter((_, i) => i !== index));
  const updateKeywordLink = (index, field, value) => {
    const updated = [...keywordLinks];
    updated[index][field] = value;
    setKeywordLinks(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) {
      toast.error('Description is required');
      return;
    }

    setSubmitting(true);
    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'packages') {
          formData.packages.forEach(val => data.append('packages[]', val));
        } else {
          data.append(key, formData[key] || '');
        }
      });
      data.append('description', description);
      if (image) data.append('image', image);

      faqs.filter(f => f.question.trim() || f.answer.trim()).forEach((faq, i) => {
        data.append(`faq[${i}][question]`, faq.question);
        data.append(`faq[${i}][answer]`, faq.answer);
      });

      recentFaqs.filter(f => f.question.trim() || f.answer.trim()).forEach((faq, i) => {
        data.append(`recentFaq[${i}][question]`, faq.question);
        data.append(`recentFaq[${i}][answer]`, faq.answer);
      });

      keywordLinks.filter(k => k.keyword.trim() || k.link.trim()).forEach((kl, i) => {
        data.append(`keywordLink[${i}][keyword]`, kl.keyword);
        data.append(`keywordLink[${i}][link]`, kl.link);
      });

      const response = await apiService.postFormData('updateBlog', data);
      if (!response.success) {
        toast.success('Blog updated successfully');
        navigate('/view-blogs');
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-0">
      <nav className="flex flex-wrap items-center text-xs sm:text-sm mb-3">
        <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">Home</Link>
        <span className="mx-2 text-gray-400">/</span>
        <button type="button" onClick={() => setShowBackModal(true)} className="text-gray-500 hover:text-gray-700">Blogs</button>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-900 font-medium">Add Blog</span>
      </nav>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-6">
          <div className="md:col-span-4">
            <label className="block text-sm font-medium mb-2">Image</label>
            <div className="relative w-full max-w-xs mx-auto md:mx-0 h-44 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
              {imagePreview ? (
                <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
              <input type="file" accept="image/*" onChange={handleImageChange} required className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
            </div>
          </div>
          <div className="md:col-span-8 space-y-4">
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Title <span className="text-red-500">*</span></label>
              <input type="text" required value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg" placeholder="Blog title" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Sort Description <span className="text-red-500">*</span></label>
              <input type="text" required value={formData.sortDescription} onChange={(e) => setFormData({ ...formData, sortDescription: e.target.value })} className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg" placeholder="Sort Description" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium mb-2">Latitude</label>
            <input type="number" step="any" value={formData.latitude} onChange={(e) => setFormData({ ...formData, latitude: e.target.value })} className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg" placeholder="Enter latitude" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Longitude</label>
            <input type="number" step="any" value={formData.longitude} onChange={(e) => setFormData({ ...formData, longitude: e.target.value })} className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg" placeholder="Enter longitude" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Blogger <span className="text-red-500">*</span></label>
            <input type="text" required value={formData.writer} onChange={(e) => setFormData({ ...formData, writer: e.target.value })} className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg" placeholder="Blogger Name" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Blog Category</label>
            <select value={formData.blogCategory} onChange={(e) => setFormData({ ...formData, blogCategory: e.target.value })} className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg">
              <option value="">Select blog category</option>
              <option value="user">User</option>
              <option value="agent">Agent</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Type <span className="text-red-500">*</span></label>
            <select required value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg">
              <option value="travellinglark">TravellingLark</option>
              <option value="woltrix">Woltrix</option>
            </select>
          </div>
          <div className="sm:col-span-2 md:col-span-3">
            <label className="block text-sm font-medium mb-2">Packages</label>
            <MultiSelect
              options={packages}
              value={formData.packages || []}
              onChange={(value) => setFormData({ ...formData, packages: value })}
              placeholder="Select packages"
            />
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Description <span className="text-red-500">*</span></label>
          <JoditEditor ref={descriptionEditor} value={description} config={editorConfig} onBlur={newContent => setDescription(newContent)} />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Meta Title</label>
          <input type="text" value={formData.metaTitle} onChange={(e) => setFormData({ ...formData, metaTitle: e.target.value })} className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg" placeholder="Enter meta title" />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Meta Script (Schema)</label>
          <textarea value={formData.metaScript} onChange={(e) => setFormData({ ...formData, metaScript: e.target.value })} className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg" rows="3" placeholder="Type your text here..."></textarea>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Meta Keywords</label>
          <textarea value={formData.metaKeyword} onChange={(e) => setFormData({ ...formData, metaKeyword: e.target.value })} className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg" rows="3" placeholder="Enter meta keywords"></textarea>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Meta Description</label>
          <textarea value={formData.metaDescription} onChange={(e) => setFormData({ ...formData, metaDescription: e.target.value })} className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg" rows="3" placeholder="Enter meta Description"></textarea>
        </div>

        <div className="mb-6">
          <div className="mb-4">
            <label className="text-base sm:text-lg font-semibold">FAQ Section</label>
          </div>
          {faqs.map((faq, index) => (
            <div key={index} className="border rounded-lg p-4 mb-3">
              <div className="mb-3">
                <label className="block text-sm font-medium mb-2">Question</label>
                <input type="text" value={faq.question} onChange={(e) => updateFaq(index, 'question', e.target.value)} className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg" placeholder="Enter FAQ question" />
              </div>
              <div className="mb-3">
                <label className="block text-sm font-medium mb-2">Answer</label>
                <textarea value={faq.answer} onChange={(e) => updateFaq(index, 'answer', e.target.value)} className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg" rows="3" placeholder="Enter FAQ answer"></textarea>
              </div>
              <button type="button" onClick={() => removeFaq(index)} className="px-3 py-1 bg-red-600 text-white rounded text-sm">Remove FAQ</button>
            </div>
          ))}
          <button type="button" onClick={addFaq} className="px-3 py-1 bg-[#009ef7] text-white rounded text-sm hover:bg-[#009ef7]/90 whitespace-nowrap">Add FAQ</button>
        </div>

        <div className="mb-6">
          <div className="mb-4">
            <label className="text-base sm:text-lg font-semibold">Recent FAQ Section</label>
          </div>
          {recentFaqs.map((faq, index) => (
            <div key={index} className="border rounded-lg p-4 mb-3">
              <div className="mb-3">
                <label className="block text-sm font-medium mb-2">Question</label>
                <input type="text" value={faq.question} onChange={(e) => updateRecentFaq(index, 'question', e.target.value)} className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg" placeholder="Enter Recent FAQ question" />
              </div>
              <div className="mb-3">
                <label className="block text-sm font-medium mb-2">Answer</label>
                <textarea value={faq.answer} onChange={(e) => updateRecentFaq(index, 'answer', e.target.value)} className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg" rows="3" placeholder="Enter Recent FAQ answer"></textarea>
              </div>
              <button type="button" onClick={() => removeRecentFaq(index)} className="px-3 py-1 bg-red-600 text-white rounded text-sm">Remove Recent FAQ</button>
            </div>
          ))}
          <button type="button" onClick={addRecentFaq} className="px-3 py-1 bg-[#009ef7] text-white rounded text-sm hover:bg-[#009ef7]/90 whitespace-nowrap">Add Recent FAQ</button>
        </div>

        <div className="mb-6">
          <div className="mb-4">
            <label className="text-base sm:text-lg font-semibold">Keyword Link Section</label>
          </div>
          {keywordLinks.map((kl, index) => (
            <div key={index} className="border rounded-lg p-4 mb-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-sm font-medium mb-2">Keyword</label>
                  <input type="text" value={kl.keyword} onChange={(e) => updateKeywordLink(index, 'keyword', e.target.value)} className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg" placeholder="Enter keyword" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Link</label>
                  <input type="url" value={kl.link} onChange={(e) => updateKeywordLink(index, 'link', e.target.value)} className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg" placeholder="Enter link URL" />
                </div>
              </div>
              <button type="button" onClick={() => removeKeywordLink(index)} className="px-3 py-1 bg-red-600 text-white rounded text-sm">Remove</button>
            </div>
          ))}
          <button type="button" onClick={addKeywordLink} className="px-3 py-1 bg-[#009ef7] text-white rounded text-sm hover:bg-[#009ef7]/90 whitespace-nowrap">Add Keyword Link</button>
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t">
          <button
            type="button"
            onClick={() => setShowBackModal(true)}
            className="px-2 sm:px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 text-gray-700 text-center text-sm font-medium"
          >
            Cancel
          </button>
          <button type="submit" disabled={submitting} className="px-2 sm:px-4 py-2 bg-[#009ef7] text-white rounded-lg hover:bg-[#009ef7]/90 disabled:opacity-50">
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </form>

      <ConfirmModal
        isOpen={showBackModal}
        title="Are you sure you want to go back?"
        message="Any unsaved blog information will be lost if you leave this page."
        confirmText="Yes, Go Back"
        cancelText="Stay Here"
        onConfirm={() => navigate('/view-blogs')}
        onCancel={() => setShowBackModal(false)}
      />
    </div>
  );
};

export default AddBlog;

